/**
 * Converts a UI ApprovalWorkflow to an ApiApprovalWorkflow for backend submission.
 * - Converts expenseCategory dimensions to customField with the given customFieldId.
 */
import {
  type ApprovalWorkflow,
  type ApprovalWorkflowNode,
  type ApprovalWorkflowBranch,
  type ApiApprovalWorkflow,
  type ApiApprovalWorkflowNode,
  type ApiApprovalWorkflowBranch,
  type ApiDimension,
} from '../approvalWorkflow';

export function convertApprovalWorkflowToApi(
  workflow: ApprovalWorkflow,
  expenseCategoryCustomFieldId: string | undefined,
): ApiApprovalWorkflow {
  function convertNode(node: ApprovalWorkflowNode): ApiApprovalWorkflowNode {
    let branch: ApiApprovalWorkflowBranch | undefined;
    if (node.branch) {
      branch = convertBranch(node.branch);
    }
    return {
      ...node,
      values: node.values ?? null,
      branch,
    };
  }

  function convertBranch(
    branch: ApprovalWorkflowBranch,
  ): ApiApprovalWorkflowBranch {
    let dimension: ApiDimension;
    if (branch.dimension.type === 'expenseCategory') {
      if (!expenseCategoryCustomFieldId) {
        throw new Error(
          `Missing expenseCategoryCustomFieldId for workflow: ${workflow.id}`,
        );
      }
      dimension = {
        type: 'customField',
        customFieldId: expenseCategoryCustomFieldId,
      };
    } else if (branch.dimension.type === 'customField') {
      dimension = branch.dimension;
    } else {
      const { type } = branch.dimension;
      if (type === 'spendType' || type === 'costCenter' || type === 'team') {
        dimension = { type };
      } else {
        throw new Error(`Invalid dimension type for API: ${type}`);
      }
    }
    return {
      dimension,
      nodes: branch.nodes.map((node) => convertNode(node)),
    };
  }

  return {
    id: workflow.id,
    definition: {
      values: null,
      outcome: workflow.definition.outcome,
      branch: workflow.definition.branch
        ? convertBranch(workflow.definition.branch)
        : undefined,
    },
  };
}
